"use client"

import { useEffect, useState } from "react"

import api from "@/lib/axios"
import {
    EVENT_DATE_ISO,
    EVENT_DATE_LABEL,
    EVENT_LOCATION_LABEL,
    EVENT_SEAT_CAP,
} from "@/lib/free-mock-event"

export interface FreeMockSlotPublic {
    event_key: string
    starts_at: string
    date_label: string
    time_label: string
    venue_name?: string
    location_text: string
    map_link?: string | null
    poster_url?: string | null
    title?: string | null
    description?: string | null
    notes?: string | null
    seat_cap?: number | null
    price?: number
    base_price?: number
    pricing_mode?: "free" | "paid"
    currency?: string
    early_bird_price?: number
    early_bird_enabled?: boolean
    is_early_bird?: boolean
}

/** What the constants said before the slot became editable — used until the
 *  fetch resolves, and if it fails, so the page never renders a blank date. */
const FALLBACK: FreeMockSlotPublic = {
    event_key: "fallback",
    starts_at: EVENT_DATE_ISO,
    date_label: EVENT_DATE_LABEL,
    time_label: "",
    venue_name: "DSAT School Campus",
    location_text: EVENT_LOCATION_LABEL,
    map_link: null,
    poster_url: null,
    title: "In-house mock test",
    description:
        "Sit a full-length Digital SAT under real exam conditions — properly timed, properly supervised — and find out where you actually stand before the real thing.",
    seat_cap: EVENT_SEAT_CAP,
    price: 0,
    base_price: 0,
    pricing_mode: "free",
    currency: "BDT",
    early_bird_price: 0,
    early_bird_enabled: false,
    is_early_bird: false,
}

/**
 * The sitting currently being advertised.
 *
 * Fetched rather than hardcoded so the admin can move the date without a
 * deploy. Starts from the previous constants so there is no flash of empty
 * text while the request is in flight.
 */
export function useFreeMockSlot() {
    const [slot, setSlot] = useState<FreeMockSlotPublic>(FALLBACK)
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        let alive = true
        api.get<FreeMockSlotPublic>("/free-mock/slot")
            .then((res) => alive && setSlot(res.data))
            .catch(() => {
                /* keep the fallback */
            })
            .finally(() => alive && setLoaded(true))
        return () => {
            alive = false
        }
    }, [])

    return { slot, loaded }
}


/**
 * How far away a slot is, in words.
 *
 * The clock read lives here rather than in a component body: it is impure, and
 * calling it during render is both a lint error and a hydration hazard. Callers
 * gate on a mounted flag, so the server never renders one of these strings.
 */
export function describeCountdown(startsAtIso: string): string | null {
    const target = new Date(startsAtIso).getTime()
    if (Number.isNaN(target)) return null
    const days = Math.max(0, Math.floor((target - Date.now()) / 86_400_000))
    if (Date.now() > target) return "Registration for this date has closed"
    if (days === 0) return "Happening today"
    if (days === 1) return "Tomorrow — last chance to reserve"
    return `${days} days away`
}

/** Whether a slot's start time is in the past. Same impurity note as above. */
export function hasSlotPassed(startsAtIso: string): boolean {
    const target = new Date(startsAtIso).getTime()
    return !Number.isNaN(target) && Date.now() > target
}
